<script>
	import countries from 'data/countries';
	import _ from 'underscore';
	import utilities from 'utilities';
	import discoveryService from 'services/discoveryService';
	import donationUtilities from 'utilities/donationUtilities';
	import metadata from 'core/metadata';
	import accountUtilities from 'utilities/accountUtilities';
	import page from 'page';

	import { fireEvent, events } from 'utilities/analyticsUtilities';
	const { ONBOARDING } = events;

	import GradeSelector from 'components/grade-selector.vue';
	import ItemSelector from 'components/item-selector.vue';
	import ValidatedInput from 'components/validated-input.vue';
	import ToggleButtons from 'components/toggle-buttons.vue';
	import LoadMask from 'components/load-mask.vue';
	import Message from 'components/message.vue';

	const SEPARATOR = {
		text: '-----------------',
		value: null,
		disabled: true
	};
	const CANT_FIND_MY_SCHOOL = {
		text: "I can't find my school.",
		value: 'NOT_FOUND'
	};

	export default {
		modelData: ['userContext', 'account', 'signUpReturnUrl'],
		components: {
			'grade-selector': GradeSelector,
			'item-selector': ItemSelector,
			'validated-input': ValidatedInput,
			'toggle-buttons': ToggleButtons,
			'load-mask': LoadMask,
			message: Message
		},
		data() {
			return {
				loading: false,
				showLoadMask: true,
				accountLoaded: false,

				// Role & Grades
				role: '',
				roles: [...metadata.roles],
				grades: [],
				curriculum: [],
				ssoSystems: [],

				// School info
				schoolData: null,
				schoolZip: null,
				schoolName: null,
				schoolNameMap: {},
				schoolId: '',
				states: countries.US.children.map(state => ({
					value: state,
					text: state
				})),
				city: null,
				state: '',
				k12: true,
				zipRegex: utilities.regexp.zipcode
			};
		},
		computed: {
			gradeSelections() {
				return this.getMappedSelections('grades', 'gradeSelections');
			},
			curriculumSelections() {
				return this.getMappedSelections('curriculum', 'curriculum');
			},
			ssoSelections() {
				return this.getMappedSelections('ssoSystems', 'ssoSystems');
			},
			isParent() {
				return this.role === 385;
			},
			isProfileComplete() {
				const role = this.role;
				const k12 = this.k12;
				const schoolId = this.schoolId;
				const schoolInfo = !k12 || !!schoolId;
				return role && schoolInfo;
			},
			schools() {
				if (this.schoolData?.length) {
					return this.schoolData;
				}
				return this.getInitialSchoolsList();
			},
			schoolPlaceholder() {
				let placeholder = 'I work at';
				if (this.isParent) {
					placeholder = 'My child attends';
				}
				if (!this.schoolZip && !this.schoolId) {
					placeholder = 'Enter ZIP code to choose a school';
				}
				return placeholder;
			},
			firstName() {
				const userContext = this.userContext;
				return userContext ? userContext.firstName : '';
			},
			k12Legend() {
				return this.isParent
					? 'Does your child attend a PreK - 12 School/District? <span class="font-small text-muted">(optional)</span>'
					: 'Do you work in a U.S. PreK - 12 School/District?';
			},
			k12Options() {
				const k12 = this.k12;
				return [
					{
						label: 'Yes',
						value: true,
						selected: k12
					},
					{
						label: 'No',
						value: false,
						selected: !k12
					}
				];
			}
		},
		methods: {
			async updateAccount() {
				const role = this.role;
				const grades = this.grades;
				const k12 = this.k12;
				let schoolId = this.schoolId ?? '';
				let schoolName = '';
				const country = 'US';
				const city = this.city ?? '';
				const state = this.state ?? '';
				const curriculum = this.curriculum ?? [];
				const ssoSystems = this.ssoSystems ?? [];

				if (schoolId && schoolId !== 'NOT_FOUND') {
					schoolName = this.schoolNameMap[schoolId];
				} else if (schoolId === 'NOT_FOUND') {
					schoolId = '';
					schoolName = this.schoolName;
				}

				let data = {
					k12,
					role,
					grades,
					curriculum,
					ssoSystems
				};
				if (k12 && schoolName) {
					data.schoolName = schoolName;
					if (schoolId) {
						data.schoolId = schoolId;
					}
				} else if (!k12) {
					data.city = city;
					data.state = state;
					data.country = country;
				}

				this.showLoadMask = true;
				await accountUtilities.update(data);
				this.showLoadMask = false;

				this.eventClick('continue');
				donationUtilities.showDonorboxModalOnce({
					role,
					other: true
				});

				const url = this.signUpReturnUrl || '/onboarding-class-name';
				page(url);
			},
			onItemSelection(key, item) {
				const itemIndex = this[key].indexOf(item.id);
				if (itemIndex === -1) {
					this[key].push(item.id);
				} else {
					this[key].splice(itemIndex, 1);
				}
			},
			eventClick(action_type) {
				fireEvent(ONBOARDING, {
					step: 'role',
					action_type
				});
			},
			updateK12({ value: k12 }) {
				this.k12 = k12;

				if (!k12) {
					donationUtilities.showDonorboxModalOnce({
						nonK12: true
					});
				}

				this.schoolId = '';
				this.schoolName = '';
				this.schoolZip = null;
				this.city = null;
				this.state = '';
			},
			getInitialSchoolsList() {
				if (this.account?.schoolId) {
					return [
						{
							text: this.account.schoolName,
							value: this.account.schoolId
						},
						SEPARATOR,
						CANT_FIND_MY_SCHOOL
					];
				}
				return [];
			},
			getMappedSelections(formKey, metadataKey) {
				const selectedItems = new Set(this[formKey]);

				return metadata[metadataKey].map(item => ({
					...item,
					selected: selectedItems.has(item.id)
				}));
			}
		},
		watch: {
			role(role) {
				if (!this.accountLoaded && donationUtilities.isRoleThatShowsModal(role)) {
					donationUtilities.showDonorboxModalOnce({
						role
					});
				}
				this.accountLoaded = false;
			},
			schoolZip: _.debounce(async function (zip) {
				if (utilities.regexp.zipcode.test(zip)) {
					this.loading = true;
					const { hits } = await discoveryService.searchSchool({
						zip
					});
					await discoveryService.searchSchool({
						zip
					});

					if (hits) {
						this.schoolId = '';

						let { schools, schoolNameMap } = hits.reduce(
							(acc, { id: value, name: text }) => {
								acc.schoolNameMap[value] = text;
								acc.schools.push({
									value,
									text
								});
								return acc;
							},
							{ schools: [], schoolNameMap: {} }
						);

						schools = schools.sort((a, b) => a.text.localeCompare(b.text, { sensitivity: 'base' }));

						this.schoolId = '';
						this.schoolData = [...schools, SEPARATOR, CANT_FIND_MY_SCHOOL];
						this.schoolNameMap = schoolNameMap;

						this.loading = false;
					}

					this.zipCodeError = null;
				} else {
					this.zipCodeError = zip ? 'Invalid zipcode.' : null;
					this.schoolId = '';
				}
			}, 500)
		},
		created() {
			accountUtilities.get().then(({ role, grades, schoolId, k12, city, state, curriculum, ssoSystems }) => {
				this.showLoadMask = false;

				if (role) {
					this.role = `_${role}` in metadata.rolesById ? role : '';
					this.accountLoaded = true;
				}

				if (grades) {
					this.grades = grades;
					this.accountLoaded = true;
				}

				if (schoolId) {
					this.schoolId = schoolId;
					this.accountLoaded = true;
				}

				if (curriculum) {
					this.curriculum = curriculum;
				}

				if (ssoSystems) {
					this.ssoSystems = ssoSystems;
				}

				if (k12 === false) {
					this.k12 = k12;
					this.city = city;
					this.state = state;
				}
			});
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';

	.role-info {
		.main-content {
			max-width: 50rem;
			margin: 0 auto;
		}

		.asterisk {
			color: $c-red;
		}

		.hi-name {
			color: $c-slate-dark;
			font-size: 1.75rem;
			font-weight: 400;
			margin-bottom: 0;

			+ p {
				margin-top: 0;
			}
		}

		[aria-invalid='true'] {
			border-color: $c-red;
		}
	}
</style>
<template>
	<main
		id="content"
		class="main role-info onboarding">
		<div class="main-content wrapper">
			<load-mask v-if="showLoadMask"></load-mask>
			<h1
				v-if="firstName"
				class="hi-name">
				Hi {{ firstName }}!
			</h1>
			<p>Welcome to ReadWorks! We want to help you curate the best content for your classroom.<br />Tell us about yourself.</p>
			<form
				class="form"
				@submit.prevent="updateAccount">
				<div class="form-body">
					<h2 class="panel-title">Tell us about your role<span class="asterisk">*</span></h2>
					<div class="margin-bottom-smedium role">
						<label for="onboarding-role">I'm a</label>
						<div class="input-select">
							<select
								id="onboarding-role"
								v-model="role"
								class="role-input"
								required>
								<option
									disabled
									value=""></option>
								<option
									v-for="role in roles"
									:key="role.value"
									:value="role.value">
									{{ role.text }}
								</option>
							</select>
						</div>
					</div>
					<grade-selector
						class="margin-bottom-smedium grades"
						legend="My focus is on:"
						:grades="gradeSelections"
						:on-grade-click="grade => onItemSelection('grades', grade)"
						prek-view>
					</grade-selector>
					<hr />
					<h2>Tell us about your school/district<span class="asterisk">*</span></h2>
					<div class="margin-bottom-smedium">
						<toggle-buttons
							class="onboarding-k12"
							:legend="k12Legend"
							:options="k12Options"
							@click-item="updateK12"></toggle-buttons>
					</div>
					<div v-if="k12">
						<div class="margin-bottom-smedium zip">
							<label for="onboarding-zip">My school zip code is</label>
							<input
								type="text"
								id="onboarding-zip"
								class="input"
								:pattern="zipRegex.source"
								:required="!schoolId"
								:aria-invalid="!!zipCodeError?.length"
								aria-describedby="zip-code-error-message"
								v-model.trim="schoolZip" />
							<message
								v-if="!!zipCodeError?.length"
								type="error">
								<ul
									id="zip-code-error-message"
									class="list-unstyled">
									<li v-text="zipCodeError"></li>
								</ul>
							</message>
						</div>
						<div
							class="margin-bottom-smedium school-id"
							v-show="schoolZip || schoolId">
							<label
								for="onboarding-schoolid"
								v-text="schoolPlaceholder"></label>
							<div class="input-select">
								<select
									v-model="schoolId"
									id="onboarding-schoolid"
									:disabled="!schools || schools.length < 1"
									required>
									<option
										disabled
										value=""></option>
									<option
										v-for="school in schools"
										:key="school.value"
										:value="school.value"
										:disabled="school.disabled === true">
										{{ school.text }}
									</option>
								</select>
							</div>
						</div>
						<div
							v-if="schoolId === 'NOT_FOUND'"
							class="margin-bottom-smedium">
							<label for="onboarding-schoolname">School Name</label>
							<validated-input
								id="onboarding-schoolname"
								type="text"
								class="school-name"
								maxlength="64"
								v-model.trim="schoolName"></validated-input>
						</div>
						<h2 class="margin-top-large">Additional Details</h2>
						<item-selector
							class="margin-bottom-smedium curriculum-selector"
							legend="Please share your Curriculum/Standards"
							:items="curriculumSelections"
							:on-item-click="item => onItemSelection('curriculum', item)">
						</item-selector>
						<item-selector
							class="margin-bottom-smedium sso-systems-selector"
							legend="Please share your SSO system(s)"
							:items="ssoSelections"
							:on-item-click="item => onItemSelection('ssoSystems', item)">
						</item-selector>
					</div>
					<div v-else>
						<div class="margin-bottom-smedium city-town">
							<label for="onboarding-city">City/Town</label>
							<validated-input
								id="onboarding-city"
								type="text"
								class="city"
								maxlength="64"
								v-model.trim="city"></validated-input>
						</div>
						<div class="margin-bottom-smedium state">
							<label for="onboarding-state">State</label>
							<div class="input-select">
								<select
									v-model="state"
									id="onboarding-state"
									class="state">
									<option
										disabled
										value=""></option>
									<option
										v-for="state in states"
										:key="state.value"
										:value="state.value">
										{{ state.text }}
									</option>
								</select>
							</div>
						</div>
					</div>
					<button
						type="submit"
						class="onboarding-button button -full-width margin-top-bottom-large"
						:class="{
							'-in-progress': loading
						}"
						:disabled="loading || !isProfileComplete">
						Get Started!
					</button>
				</div>
			</form>
		</div>
	</main>
</template>
